// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ToastProvider } from './shared/Toast';
import './shared/modern-ux.css';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordChangePage from './pages/PasswordChangePage';
import NDASignaturePage from './pages/NDASignaturePage';
import AppTutorialPage from './pages/AppTutorialPage';
import SignUpPage from './pages/SignUpPage';
import OverwatchPage from './pages/OverwatchPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
// Product & Inventory
import ProductCatalogPage from './pages/ProductCatalogPage';
import InventoryPage from './pages/InventoryPage';
import PricingPage from './pages/PricingPage';
import SuppliersPage from './pages/SuppliersPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import ReceivingPage from './pages/ReceivingPage';
// Departments
import DepartmentsPage from './pages/DepartmentsPage';
import DeliCounterPage from './pages/DeliCounterPage';
import BakeryPage from './pages/BakeryPage';
import ProducePage from './pages/ProducePage';
import MeatSeafoodPage from './pages/MeatSeafoodPage';
// Janitorial & Compliance
import JanitorialDashboardPage from './pages/JanitorialDashboardPage';
import CleaningChecklistPage from './pages/CleaningChecklistPage';
import IncidentLogPage from './pages/IncidentLogPage';
import InspectionReportsPage from './pages/InspectionReportsPage';
import SupplyInventoryPage from './pages/SupplyInventoryPage';
// Staff
import StaffSchedulingPage from './pages/StaffSchedulingPage';
import TrainingPage from './pages/TrainingPage';
import PayrollPage from './pages/PayrollPage';
// Financial
import POSOverviewPage from './pages/POSOverviewPage';
import DailySalesPage from './pages/DailySalesPage';
import ProfitLossPage from './pages/ProfitLossPage';
import ShrinkagePage from './pages/ShrinkagePage';
// Customer-Facing
import OnlineStorePage from './pages/OnlineStorePage';
import CouponsPage from './pages/CouponsPage';
import LoyaltyProgramPage from './pages/LoyaltyProgramPage';
import DeliveryPickupPage from './pages/DeliveryPickupPage';
import WeeklyDealsPage from './pages/WeeklyDealsPage';
import ShoppingListPage from './pages/ShoppingListPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
// Marketing
import MarketingPage from './pages/MarketingPage';
import ReviewsPage from './pages/ReviewsPage';

const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com',
  'R.miskulin@miskulintechnologies.com',
  'rmiskulin@gmail.com',
  'threewayindustries@gmail.com',
  'desbachynski@gmail.com',
  'lucas.s.mason@gmail.com',
  'leemehling@hotmail.com',
  'cloricer77@gmail.com',
  'tyson@canstorr.com',
  'tyler.hodgson32@gmail.com',
  'jhansen860@yahoo.com',
  'littlesderek@gmail.com',
  'laken.weston@live.ca',
  'che@rosebudcannabisfarms.ca',
  'shanon@lanewaymedia.ca',
  'amandakristaleigh@hotmail.com',
  'dblahut@sastel.net',
  'demo@iqapps.dev',
];

export { ADMIN_TEAM };

function RoleHomePage() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (ADMIN_TEAM.map(e => e.toLowerCase()).includes((user.email || '').toLowerCase())) return <Navigate to="/overwatch" />;
  if (user.role === 'owner') return <Navigate to="/overwatch" />;
  return <Navigate to="/dashboard" />;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg)', color: 'var(--text)', fontSize: 18 }}>Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') return <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />;
  if (onboardingStep === 'nda') return <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />;
  if (onboardingStep === 'tutorial') return <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />;
  return children;
}

export default function App() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg)', color: 'var(--text)', fontSize: 18 }}>Loading...</div>;

  return (
    <ToastProvider position="top-right">
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/signup" element={user ? <Navigate to="/" /> : <SignUpPage />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/overwatch" element={<PrivateRoute><OverwatchPage /></PrivateRoute>} />
        <Route path="/" element={<PrivateRoute><OnboardingGate><Layout /></OnboardingGate></PrivateRoute>}>
          <Route index element={<RoleHomePage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="product-catalog" element={<ProductCatalogPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="receiving" element={<ReceivingPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="deli-counter" element={<DeliCounterPage />} />
          <Route path="bakery" element={<BakeryPage />} />
          <Route path="produce" element={<ProducePage />} />
          <Route path="meat-seafood" element={<MeatSeafoodPage />} />
          <Route path="janitorial" element={<JanitorialDashboardPage />} />
          <Route path="cleaning-checklist" element={<CleaningChecklistPage />} />
          <Route path="incident-log" element={<IncidentLogPage />} />
          <Route path="inspection-reports" element={<InspectionReportsPage />} />
          <Route path="supply-inventory" element={<SupplyInventoryPage />} />
          <Route path="staff-scheduling" element={<StaffSchedulingPage />} />
          <Route path="training" element={<TrainingPage />} />
          <Route path="payroll" element={<PayrollPage />} />
          <Route path="pos-overview" element={<POSOverviewPage />} />
          <Route path="daily-sales" element={<DailySalesPage />} />
          <Route path="profit-loss" element={<ProfitLossPage />} />
          <Route path="shrinkage" element={<ShrinkagePage />} />
          <Route path="online-store" element={<OnlineStorePage />} />
          <Route path="coupons" element={<CouponsPage />} />
          <Route path="loyalty-program" element={<LoyaltyProgramPage />} />
          <Route path="delivery-pickup" element={<DeliveryPickupPage />} />
          <Route path="weekly-deals" element={<WeeklyDealsPage />} />
          <Route path="shopping-list" element={<ShoppingListPage />} />
          <Route path="order-history" element={<OrderHistoryPage />} />
          <Route path="marketing" element={<MarketingPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      <Route path="/demo-management" element={<ProtectedRoute><DemoManagementPage /></ProtectedRoute>} />

      </Routes>
    </ToastProvider>
  );
}
